import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";

import { isWebActive } from "@/lib/environment";

const emailSchema = z.string().email("Email no válido");
const passwordSchema = z.string()
  .min(8, "La contraseña debe tener al menos 8 caracteres")
  .regex(/[A-Z]/, "Debe contener al menos una mayúscula")
  .regex(/[0-9]/, "Debe contener al menos un número");

interface AuthFormProps {
  initialMode?: "login" | "signup" | "forgot-password" | "update-password";
  onSuccess?: () => void;
  hideFooter?: boolean;
}

const AuthForm = ({ 
  initialMode = "login", 
  onSuccess,
  hideFooter = false 
}: AuthFormProps) => {
  const [mode, setMode] = useState<"login" | "signup" | "forgot-password" | "update-password">(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string; confirmPassword?: string; policy?: string }>({});

  const { signIn, signUp, signInWithGoogle, resetPassword, updateUserPassword, openComingSoonPopup, closeAuthModal } = useAuth();
  const { toast } = useToast();
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [signupEmailSent, setSignupEmailSent] = useState(false);
  const [isResendingEmail, setIsResendingEmail] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("type") === "recovery") {
      setMode("update-password");
    }
  }, [searchParams]);

  const navigate = useNavigate();

  const handleLoginSuccess = () => {
    if (onSuccess) {
      onSuccess();
    }
    navigate("/dashboard", { replace: true });
  };

  const handleResendConfirmationEmail = async () => {
    if (!email) {
      toast({
        title: "Error",
        description: "Por favor ingresa tu email",
        variant: "destructive",
      });
      return;
    }

    setIsResendingEmail(true);
    try {
      // Importar supabase aquí para evitar ciclo de dependencias
      const { supabase } = await import("@/integrations/supabase/client");
      
      // ✅ FIXED: Use supabase.auth.resend() instead of non-existent resendEnrollmentEmail()
      // resendEnrollmentEmail() doesn't exist in supabase-js v2 — causes silent failure
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/?confirmed=email`
        }
      });
      
      if (error) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Email reenviado",
          description: "Te hemos reenviado el email de confirmación. Revisa tu bandeja de entrada, incluyendo la carpeta de Spam.",
        });
      }
    } catch (err) {
      console.error('[AuthForm] Error resending confirmation email:', err);
      toast({
        title: "Error",
        description: "No pudimos reenviar el email. Por favor intenta más tarde.",
        variant: "destructive",
      });
    } finally {
      setIsResendingEmail(false);
    }
  };

  const validateForm = () => {
    const newErrors: { email?: string; password?: string; confirmPassword?: string; policy?: string } = {};

    if (mode !== "update-password") {
      const emailResult = emailSchema.safeParse(email);
      if (!emailResult.success) {
        newErrors.email = emailResult.error.errors[0].message;
      }
    }

    if (mode !== "forgot-password") {
      const passwordResult = passwordSchema.safeParse(password);
      if (!passwordResult.success) {
        newErrors.password = passwordResult.error.errors[0].message;
      }

      if (mode === "update-password") {
        if (password !== confirmPassword) {
          newErrors.confirmPassword = "Las contraseñas no coinciden";
        }
      }
    }

    if (mode === "signup" && !policyAccepted) {
      newErrors.policy = "Debes aceptar la política de privacidad";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    if (mode === "login") {
      let hasBeta = false;
      try {
        hasBeta = localStorage.getItem("bs_beta_access") === "true";
      } catch {
        hasBeta = false;
      }

      if (!isWebActive() && !hasBeta) {
        setIsSubmitting(false);
        closeAuthModal();
        openComingSoonPopup();
        return;
      }

      const { error } = await signIn(email, password);
      if (error) {
        toast({
          title: "Error al iniciar sesión",
          description: error.message === "Invalid login credentials"
            ? "Credenciales incorrectas"
            : error.message,
          variant: "destructive",
        });
      } else {
        handleLoginSuccess();
      }
    } else if (mode === "signup") {
      // NOTE: fullName is no longer requested here, collected in ProfileCompletionModal
       const { error, needsEmailConfirmation } = await signUp(email, password);
      if (error) {
        let errorMessage = error.message;
        if (error.message.includes("already registered")) {
          errorMessage = "Este email ya está registrado";
        }
        toast({
          title: "Error al registrarse",
          description: errorMessage,
          variant: "destructive",
        });
      } else {
         // ✅ LÓGICA CONDICIONAL: Depender de needsEmailConfirmation que viene de Supabase
         // - DEV (enable_confirmations=false): data.session !== null → needsEmailConfirmation=false → login automático
         // - PROD (enable_confirmations=true): data.session === null → needsEmailConfirmation=true → mostrar verificación
         if (needsEmailConfirmation) {
           // PRODUCCIÓN: Requerir confirmación de email antes de permitir acceso
           setSignupEmailSent(true);
           toast({
             title: "¡Cuenta creada!",
             description: "Te hemos enviado un email de confirmación. Por favor, verifica tu bandeja de entrada.",
           });
         } else {
           // DESARROLLO: Usuario logueado inmediatamente, cerrar modal y continuar con onboarding
           handleLoginSuccess();
         }
       }
    } else if (mode === "forgot-password") {
      const { error } = await resetPassword(email);
      if (error) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Email enviado",
          description: "Revisa tu bandeja de entrada para recuperar tu contraseña",
        });
        setMode("login");
      }
    } else if (mode === "update-password") {
      const { error } = await updateUserPassword(password);
      if (error) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Contraseña actualizada",
          description: "Tu contraseña ha sido cambiada correctamente",
        });
        handleLoginSuccess();
      }
    }

    setIsSubmitting(false);
  };

  const handleGoogleSignIn = async () => {
    let hasBeta = false;
    try {
      hasBeta = localStorage.getItem("bs_beta_access") === "true";
    } catch {
      hasBeta = false;
    }

    if (!isWebActive() && !hasBeta) {
      closeAuthModal();
      openComingSoonPopup();
      return;
    }

    setIsGoogleLoading(true);
    const { error } = await signInWithGoogle();
    if (error) {
      toast({
        title: "Error con Google",
        description: error.message,
        variant: "destructive",
      });
      setIsGoogleLoading(false);
    }
  };

  return (
    <div className="w-full">
      {/* Email confirmation screen shown after signup */}
      {signupEmailSent && (
        <div className="text-center py-8">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-display font-bold text-foreground mb-2">
            Verifica tu email
          </h2>
          <p className="text-muted-foreground mb-3">
            Te hemos enviado un enlace de confirmación a:
          </p>
          <p className="font-medium text-foreground bg-secondary rounded-lg px-3 py-2 inline-block mb-6">
            {email}
          </p>
          <p className="text-sm text-muted-foreground mb-6">
            Por favor, abre el enlace en tu bandeja de entrada para activar tu cuenta.
            Una vez confirmado, serás redirigido automáticamente a la siguiente fase del registro.
          </p>
          
          <div className="space-y-3 mt-6">
            <p className="text-sm text-muted-foreground">
              ¿No ves el email?
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={handleResendConfirmationEmail}
              disabled={isResendingEmail}
              className="w-full"
            >
              {isResendingEmail && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Reenviar email de confirmación
            </Button>
          </div>

          <button
            type="button"
            onClick={() => { setSignupEmailSent(false); setMode("login"); }}
            className="text-sm text-primary hover:underline mt-6 block w-full"
          >
            Volver al inicio de sesión
          </button>
        </div>
      )}

      {!signupEmailSent && (
      <>
      <div className="text-center mb-6">
        <h2 className="text-2xl font-display font-bold text-foreground mb-2">
          {mode === "login" && "Iniciar sesión"}
          {mode === "signup" && "Crear cuenta"}
          {mode === "forgot-password" && "Recuperar contraseña"}
          {mode === "update-password" && "Nueva contraseña"}
        </h2>
        <p className="text-muted-foreground">
          {mode === "login" && "Accede a tu cuenta de Brickshare"}
          {mode === "signup" && "Únete a Brickshare y comienza a jugar"}
          {mode === "forgot-password" && "Te enviaremos un email para restablecer tu acceso"}
          {mode === "update-password" && "Introduce tu nueva contraseña segura"}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {mode !== "update-password" && (
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setErrors((prev) => ({ ...prev, email: undefined }));
                }}
                className={`pl-10 ${errors.email ? "border-destructive" : ""}`}
                required
              />
            </div>
            {errors.email && (
              <p className="text-xs text-destructive">{errors.email}</p>
            )}
          </div>
        )}

        {mode !== "forgot-password" && (
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="password">
                {mode === "update-password" ? "Nueva contraseña" : "Contraseña"}
              </Label>
              {mode === "login" && (
                <button
                  type="button"
                  onClick={() => setMode("forgot-password")}
                  className="text-xs text-primary hover:underline"
                >
                  ¿Olvidaste tu contraseña?
                </button>
              )}
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setErrors((prev) => ({ ...prev, password: undefined }));
                }}
                className={`pl-10 pr-10 ${errors.password ? "border-destructive" : ""}`}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
            {errors.password && (
              <p className="text-xs text-destructive">{errors.password}</p>
            )}
          </div>
        )}

        {mode === "update-password" && (
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirmar contraseña</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Repite la contraseña"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setErrors((prev) => ({ ...prev, confirmPassword: undefined }));
                }}
                className="pl-10"
                required
              />
            </div>
            {errors.confirmPassword && (
              <p className="text-xs text-destructive">{errors.confirmPassword}</p>
            )}
          </div>
        )}

        {mode === "signup" && (
          <div className="space-y-2">
            <div className="flex items-start space-x-2">
              <Checkbox
                id="policy"
                checked={policyAccepted}
                onCheckedChange={(checked) => {
                  setPolicyAccepted(checked === true);
                  setErrors(prev => ({ ...prev, policy: undefined }));
                }}
              />
              <label
                htmlFor="policy"
                className="text-xs leading-relaxed text-muted-foreground peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Declaro ser mayor de 18 años y he leído y acepto los{" "}
                <Link to="/terminos-servicio" className="text-primary hover:underline">
                  Términos de Servicio
                </Link>{" "}
                y la{" "}
                <Link to="/privacidad" className="text-primary hover:underline">
                  Política de Privacidad
                </Link>
                .
              </label>
            </div>
            {errors.policy && (
              <p className="text-xs text-destructive">{errors.policy}</p>
            )}
          </div>
        )}

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full gradient-hero"
        >
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {mode === "login" && "Iniciar sesión"}
          {mode === "signup" && "Crear cuenta"}
          {mode === "forgot-password" && "Enviar instrucciones"}
          {mode === "update-password" && "Actualizar contraseña"}
        </Button>
      </form>

      {(mode === "login" || mode === "signup") && (
        <>
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                O continúa con
              </span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={handleGoogleSignIn}
            disabled={isGoogleLoading || isSubmitting}
            className="w-full"
          >
            {isGoogleLoading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
            )}
            Continuar con Google
          </Button>
          {mode === "signup" && (
            <p className="mt-2 text-center text-[11px] text-muted-foreground leading-normal">
              Al registrarte con Google confirmas ser mayor de 18 años y aceptas los{" "}
              <Link to="/terminos-servicio" className="underline hover:text-foreground">
                Términos
              </Link>{" "}
              y la{" "}
              <Link to="/privacidad" className="underline hover:text-foreground">
                Privacidad
              </Link>.
            </p>
          )}
        </>
      )}

      <div className="mt-6 text-center">
        {mode === "forgot-password" ? (
          <button
            type="button"
            onClick={() => setMode("login")}
            className="text-sm text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver al inicio de sesión
          </button>
        ) : mode === "update-password" ? (
          null
        ) : (
          <button
            type="button"
            onClick={() => {
              if (mode === "login" && !isWebActive()) {
                // La web aún no está activa: cerrar el modal de auth y mostrar el pop-up de waitlist
                closeAuthModal();
                openComingSoonPopup();
              } else {
                setMode(mode === "login" ? "signup" : "login");
                setErrors({});
              }
            }}
            className="text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            {mode === "login"
              ? "¿No tienes cuenta? Regístrate"
              : "¿Ya tienes cuenta? Inicia sesión"}
          </button>
        )}
      </div>
      </>
      )}
    </div>
  );
};

export default AuthForm;
